import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const CoachLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { coachLogin, coach } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  if (coach) {
    navigate("/coach/dashboard");
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    setLoading(true);
    const result = await coachLogin(email, password);
    setLoading(false);

    if (result.success) {
      toast.success("Connexion réussie !");
      navigate("/coach/dashboard");
    } else {
      toast.error(result.error || "Identifiants incorrects");
    }
  };

  return (
    <div className="min-h-screen bg-[#05050A]">
      <Header />
      
      <section className="min-h-screen flex items-center justify-center pt-20 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
          data-testid="coach-login-container"
        >
          {/* Icon */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-[#0066FF]/10 border border-[#0066FF]/30 flex items-center justify-center mx-auto mb-4">
              <User className="w-10 h-10 text-[#0066FF]" />
            </div>
            <h1 className="font-['Outfit'] text-2xl font-bold uppercase" data-testid="login-title">
              Espace Coach
            </h1>
            <p className="text-[#94A3B8] mt-2">
              Connectez-vous pour accéder à votre formation et votre profil
            </p>
          </div>

          {/* Form */}
          <form 
            onSubmit={handleSubmit} 
            className="bg-[#0F0F16] border border-white/10 p-8 space-y-6"
            data-testid="coach-login-form"
          >
            <div>
              <Label htmlFor="email" className="form-label flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                className="form-input bg-[#05050A] border-white/10 text-white placeholder:text-[#64748B] focus:border-[#0066FF] h-12"
                data-testid="coach-login-email"
              />
            </div>

            <div>
              <Label htmlFor="password" className="form-label flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Mot de passe
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="form-input bg-[#05050A] border-white/10 text-white placeholder:text-[#64748B] focus:border-[#0066FF] h-12"
                data-testid="coach-login-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="coach-login-submit"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Connexion...
                </>
              ) : (
                "Se connecter"
              )}
            </button>
          </form>

          {/* Info */}
          <div className="text-center mt-6 space-y-3">
            <p className="text-[#64748B] text-sm">
              Votre compte est créé par l&apos;administrateur APM.
            </p>
            <Link
              to="/"
              className="text-[#94A3B8] hover:text-white text-sm transition-colors inline-block"
              data-testid="back-to-site"
            >
              ← Retour au site
            </Link>
          </div>
        </motion.div>
      </section>
      
      <Footer />
    </div>
  );
};

export default CoachLogin;
